import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { normalizeEmail } from '@nsc/smartteams-shared/api/email-filter.js';
import type { SNSmartTeamEngineer } from '@nsc/servicenow/types.js';
import { SmartTeamsPartnerCompany } from '../db/SmartTeamsCompany.js';
import { SmartTeamsFieldEngineer } from '../db/SmartTeamsFieldEngineer.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';
import { engineerTypeOf } from './engineer-type.js';
import { planEngineerUpdate, worksForClient } from './fe-sync-mapping.js';

/**
 * Per-company "sync field engineers from ServiceNow".
 *
 * Starting from one SmartTeams partner company that is linked to a ServiceNow client, it reads the ServiceNow
 * engineer list and picks the engineers whose GROUPS name that client (see fe-sync-mapping.ts). Each of them who
 * already exists in SmartTeams is brought up to date:
 *
 *   - this company is added to them. ONLY this company: the groups say whether an engineer works for the company
 *     being synced, not reliably which others they work for. An external engineer who also works for Parkland gets
 *     Parkland when the Parkland company is synced, and the two simply accumulate;
 *   - their partner flag follows ServiceNow: partner → on, internal → off. When ServiceNow cannot tell (`unknown`,
 *     e.g. an engineer with no company) the flag is left exactly as it is, so a flag set by hand survives;
 *   - they are linked to their ServiceNow account when they had no link yet.
 *
 * Companies are only ever ADDED, never removed. No engineer is created (that stays with the FE import, which sets up
 * logins and passwords), and engineers that are not in this client's groups are not touched at all.
 */

/** One engineer in the report shown after a sync. */
export type SyncRowStatus = 'updated' | 'unchanged' | 'missing' | 'failed';

/** Display order of the report: problems first, then what changed, then what is missing, then what was fine already. */
const STATUS_ORDER: Record<SyncRowStatus, number> = { failed: 0, updated: 1, missing: 2, unchanged: 3 };

/** @entity FieldEngineerSyncRow */
@Entity()
export class FieldEngineerSyncRow extends AdornisEntity {
  static override _class = 'FieldEngineerSyncRow';

  static override resolveID(entity: FieldEngineerSyncRow): string {
    return entity._id;
  }

  /** The engineer's ServiceNow id. */
  @Field(() => ID) _id!: string;
  @Field(() => String) name: Maybe<string>;
  @Field(() => String) email: Maybe<string>;
  /** `updated`, `unchanged`, `missing` (not in SmartTeams) or `failed`. */
  @Field(() => String) status: Maybe<SyncRowStatus>;
  /** What happened, in plain words. */
  @Field(() => String) details: Maybe<string>;
  /** The engineer's id in SmartTeams, so the report can link to them. Empty when there is none (not in SmartTeams). */
  @Field(() => String) fieldEngineerId: Maybe<string>;

  constructor(doc: PartialEntityData<FieldEngineerSyncRow>) {
    super(doc);
  }
}

/** @entity FieldEngineerSyncResult */
@Entity()
export class FieldEngineerSyncResult extends AdornisEntity {
  static override _class = 'FieldEngineerSyncResult';

  static override resolveID(entity: FieldEngineerSyncResult): string {
    return entity._id;
  }

  @Field(() => ID) _id!: string;
  /** The ServiceNow client the sync looked for, e.g. "DELL". */
  @Field(() => String) clientName: Maybe<string>;
  /** Engineers ServiceNow lists under that client. */
  @Field(() => Int) found: Maybe<number>;
  @Field(() => Int) updated: Maybe<number>;
  @Field(() => Int) unchanged: Maybe<number>;
  /** Listed in ServiceNow but not (yet) in SmartTeams. */
  @Field(() => Int) missing: Maybe<number>;
  @Field(() => Int) failed: Maybe<number>;
  @Field(() => [FieldEngineerSyncRow]) rows: Maybe<FieldEngineerSyncRow[]>;

  constructor(doc: PartialEntityData<FieldEngineerSyncResult>) {
    super(doc);
  }
}

type EngineerDoc = EntityData<SmartTeamsFieldEngineer>;

const displayName = (engineer: SNSmartTeamEngineer): string =>
  `${engineer.first_name ?? ''} ${engineer.last_name ?? ''}`.trim() || engineer.email || engineer.id;

export const syncCompanyFieldEngineers = registerMutation({
  operationName: 'syncCompanyFieldEngineers',
  type: () => FieldEngineerSyncResult,
  params: [{ name: 'companyId', type: () => String }],
  resolve: (companyId: string) => async (): Promise<FieldEngineerSyncResult> => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsPermission.FEImportExportAdmin));
    if (!allowed) throw new ExpectedError('No permission to sync field engineers');

    const companyCollection = await getRawCollection<EntityData<SmartTeamsPartnerCompany>>(
      SmartTeamsPartnerCompany._collectionName,
    );
    const company = await companyCollection.findOne({ _id: companyId, deletedAt: null });
    if (!company) throw new ExpectedError('Company not found');
    if (!company.serviceNowClientId) {
      throw new ExpectedError(
        'This company is not linked to a ServiceNow company yet. Choose one in the company details, save, and try again.',
      );
    }

    // Everything ServiceNow-side is read first, so a ServiceNow outage fails the sync before anything is written.
    const { getClientList, getEngineerList } = await import('@nsc/servicenow/smartteam-api.js');
    let clients: Awaited<ReturnType<typeof getClientList>>;
    let engineers: SNSmartTeamEngineer[];
    try {
      [clients, engineers] = await Promise.all([getClientList(), getEngineerList()]);
    } catch (error) {
      logger.error({ error, companyId }, 'Field engineer sync: could not read ServiceNow');
      throw new ExpectedError('Could not reach ServiceNow. Nothing was changed. Please try again in a few minutes.');
    }

    const clientNameById = new Map<string, string>(clients.map(client => [client.id, client.name]));
    const clientName = clientNameById.get(company.serviceNowClientId);
    if (!clientName) {
      throw new ExpectedError(
        'The ServiceNow company linked to this company no longer exists. Choose another one and try again.',
      );
    }

    // The SmartTeams engineers, indexed once rather than queried per engineer.
    const engineerCollection = await getRawCollection<EngineerDoc>(SmartTeamsFieldEngineer._collectionName);
    const existing = await engineerCollection.find({ deletedAt: null }).toArray();
    const bySysId = new Map<string, EngineerDoc>();
    const byEmail = new Map<string, EngineerDoc[]>();
    for (const fe of existing) {
      if (fe.serviceNowSysId) bySysId.set(fe.serviceNowSysId, fe);
      const email = fe.email ? normalizeEmail(fe.email) : '';
      if (email) byEmail.set(email, [...(byEmail.get(email) ?? []), fe]);
    }

    const inScope = engineers.filter(engineer => worksForClient(engineer.groups, clientName));
    const rows: FieldEngineerSyncRow[] = [];
    const row = (engineer: SNSmartTeamEngineer, status: SyncRowStatus, details: string, fieldEngineerId?: string) =>
      rows.push(
        new FieldEngineerSyncRow({
          _id: engineer.id,
          name: displayName(engineer),
          email: engineer.email || undefined,
          status,
          details,
          fieldEngineerId,
        }),
      );

    for (const engineer of inScope) {
      let fe: EngineerDoc | undefined;
      try {
        fe = bySysId.get(engineer.id);
        if (!fe && engineer.email) {
          const sameEmail = byEmail.get(normalizeEmail(engineer.email)) ?? [];
          if (sameEmail.length > 1) {
            row(engineer, 'failed', 'Several engineers in SmartTeams share this email address. Please fix this by hand.');
            continue;
          }
          fe = sameEmail[0];
          if (fe?.serviceNowSysId && fe.serviceNowSysId !== engineer.id) {
            row(engineer, 'failed', 'This email address is already linked to a different ServiceNow account.', fe._id);
            continue;
          }
        }
        if (!fe) {
          row(engineer, 'missing', 'Not in SmartTeams yet. Add them with FE Import / Export.');
          continue;
        }

        const plan = planEngineerUpdate({
          existing: fe,
          companyId,
          serviceNowType: engineerTypeOf(engineer).type,
        });
        if (!plan.changed) {
          row(engineer, 'unchanged', 'Already up to date.', fe._id);
          continue;
        }

        // Same save path as the CSV import: saving through the entity also runs the hook that keeps the engineer's
        // login record in step with the company.
        const updated = new SmartTeamsFieldEngineer({
          ...fe,
          partnerCompanyIds: plan.addCompany ? [...(fe.partnerCompanyIds ?? []), companyId] : fe.partnerCompanyIds,
        });
        if (plan.newIsPartner !== undefined) updated.isPartner = plan.newIsPartner;
        if (plan.linkServiceNowId) updated.serviceNowSysId = engineer.id;
        await updated.save();

        const changes: string[] = [];
        if (plan.addCompany) changes.push(`Added to ${company.name ?? clientName}.`);
        if (plan.newIsPartner === true) changes.push('Marked as a partner engineer.');
        if (plan.newIsPartner === false) changes.push('Changed from partner engineer to internal.');
        if (plan.linkServiceNowId) changes.push('Linked to their ServiceNow account.');
        row(engineer, 'updated', changes.join(' '), fe._id);
      } catch (error) {
        logger.error({ error, serviceNowId: engineer.id }, 'Field engineer sync: could not update an engineer');
        row(engineer, 'failed', error instanceof Error ? error.message : 'Unknown error', fe?._id);
      }
    }

    rows.sort(
      (a, b) =>
        STATUS_ORDER[a.status as SyncRowStatus] - STATUS_ORDER[b.status as SyncRowStatus] ||
        (a.name ?? '').localeCompare(b.name ?? ''),
    );
    const count = (status: SyncRowStatus) => rows.filter(r => r.status === status).length;
    logger.info(
      { companyId, clientName, found: inScope.length, updated: count('updated'), failed: count('failed') },
      'Field engineer sync finished',
    );

    return new FieldEngineerSyncResult({
      _id: 'field-engineer-sync-result',
      clientName,
      found: inScope.length,
      updated: count('updated'),
      unchanged: count('unchanged'),
      missing: count('missing'),
      failed: count('failed'),
      rows,
    });
  },
});
